/**
 * Market time (NEM time) helpers.
 *
 * The NEM runs on AEST (UTC+10) all year — no daylight saving. Interval data
 * (the 288 five-minute slots, daily totals, settlement) is counted on that
 * clock, so screens must bucket readings and count "intervals so far" in
 * market time, never on the viewer's clock: that one moves an hour at every
 * DST switch in NSW/VIC/TAS/ACT (half an hour in SA) and is a different day
 * altogether in India.
 *
 * The market clock is read through Intl (the browser's IANA zone data), not
 * dayjs .tz(): .tz() rebuilds the time in the viewer's zone, so a market time
 * that does not exist on the viewer's clock comes out an hour late — 02:00–
 * 02:55 AEST on 4 Oct 2026 rendered as 03:00–03:55 for viewers in Sydney and
 * Adelaide. Arithmetic is then done on the market wall clock expressed as a
 * UTC instant, which has no DST because the market zone has none.
 */
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

export const MARKET_TZ = "Australia/Brisbane";
export const MARKET_TZ_LABEL = "AEST";
export const INTERVALS_PER_DAY = 288;
const INTERVAL_MS = 5 * 60 * 1000;

const MARKET_CLOCK = new Intl.DateTimeFormat("en-GB", {
  timeZone: MARKET_TZ,
  hourCycle: "h23",
  year: "numeric", month: "2-digit", day: "2-digit",
  hour: "2-digit", minute: "2-digit", second: "2-digit",
});

/** The market wall-clock time of `ts`, as a UTC instant (so the fields read back with getUTC* / dayjs.utc). */
function marketWall(ts) {
  const f = {};
  for (const p of MARKET_CLOCK.formatToParts(new Date(dayjs(ts).valueOf()))) f[p.type] = Number(p.value);
  return Date.UTC(f.year, f.month - 1, f.day, f.hour, f.minute, f.second);
}

/**
 * Today's market date, as a plain local dayjs at midnight of that calendar
 * date — the shape antd's DatePicker expects.
 */
export const marketToday = () => dayjs(dayjs.utc(marketWall(Date.now())).format("YYYY-MM-DD"));

/** Position (0–287) of `ts` within market day `date` (YYYY-MM-DD); -1 if outside it. */
export function marketInterval(ts, date) {
  const i = Math.floor((marketWall(ts) - Date.parse(`${date}T00:00:00Z`)) / INTERVAL_MS);
  return i >= 0 && i < INTERVALS_PER_DAY ? i : -1;
}

/** Index of the market interval in progress now. */
export function currentMarketInterval() {
  const now = new Date(marketWall(Date.now()));
  return now.getUTCHours() * 12 + Math.floor(now.getUTCMinutes() / 5);
}

/** `ts` formatted on the market clock, e.g. "13:05". */
export const formatMarketTime = (ts, fmt = "HH:mm") => dayjs.utc(marketWall(ts)).format(fmt);

/** The viewer's own zone name at `ts`, e.g. "AEDT", "ACST", "GMT+5:30". */
export function localZoneLabel(ts) {
  try {
    const part = new Intl.DateTimeFormat("en-AU", { timeZoneName: "short" })
      .formatToParts(new Date(dayjs(ts).valueOf()))
      .find((p) => p.type === "timeZoneName");
    return part ? part.value : "";
  } catch {
    return "";
  }
}

/** `ts` in the viewer's own time, with the zone shown so nobody has to guess which clock it is. */
export function formatLocalWithZone(ts, fmt = "YYYY-MM-DD HH:mm:ss") {
  const zone = localZoneLabel(ts);
  return zone ? `${dayjs(ts).format(fmt)} ${zone}` : dayjs(ts).format(fmt);
}
